import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Theme = "dark" | "light";
const STORAGE_KEY = "klutch-theme";

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitial(): Theme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    /* ignore */
  }
  return "dark"; // Garage Dark default
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readInitial);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#0E1116" : "#F4F2EF");
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  // Print on paper, not on a screen. Every colour is a CSS variable re-pointed by
  // data-theme, so switching the attribute for the duration of the print job gives
  // the whole app a light palette without any print-only colour rules.
  //
  // Without this, Garage Dark prints unreadably: browsers drop backgrounds when
  // printing but keep the text colour, so near-white ink lands on white paper. The
  // attribute is restored on afterprint (and the theme effect above would restore
  // it anyway on the next toggle).
  useEffect(() => {
    const toPaper = () => {
      document.documentElement.dataset.theme = "light";
    };
    const restore = () => {
      document.documentElement.dataset.theme = theme;
    };
    window.addEventListener("beforeprint", toPaper);
    window.addEventListener("afterprint", restore);
    return () => {
      window.removeEventListener("beforeprint", toPaper);
      window.removeEventListener("afterprint", restore);
    };
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggle = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);

  const value = useMemo(() => ({ theme, toggle, setTheme }), [theme, toggle, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}
